import { useAddSocialMediaBulk } from "@entities/brief/lib/hooks/useAddSocialMediaBulk";
import modalStyles from "@features/AddCreator/ui/BulkAddModal/styles.module.scss";
import uploadStyles from "@features/AddCreator/ui/BulkAddModal/upload.module.scss";
import { ToastService } from "@shared/lib/services";
import { FirebaseService } from "@shared/lib/services/firebase.service";
import { IAddSocialMediaBulkResponseRow } from "@shared/lib/types/influencer";
import { Button, Modal } from "@shared/ui";
import { Spin } from "antd";
import { Download, FileSpreadsheet, Trash2 } from "lucide-react";
import React, { useRef, useState } from "react";
import { CreatorRowsPreview } from "./CreatorRowsPreview";

export interface IParsedCreatorRow {
  platform: "instagram" | "youtube";
  username: string;
}

export interface UploadSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (creators: IParsedCreatorRow[]) => void | Promise<void>;
  isImporting: boolean;
  existingUsernames?: string[];
}

export function downloadCreatorFitTemplateCsv() {
  const csvContent =
    "platform,link\ninstagram,https://www.instagram.com/selenagomez\nyoutube,https://www.youtube.com/@mrbeast\n";
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "creator_fit_template.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const UploadSheetModal: React.FC<UploadSheetModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isImporting,
  existingUsernames = [],
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validRows, setValidRows] = useState<IAddSocialMediaBulkResponseRow[]>([]);
  const [failedRows, setFailedRows] = useState<IAddSocialMediaBulkResponseRow[]>([]);
  const [activeTab, setActiveTab] = useState<"ready" | "failed">("ready");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setFile(null);
    setValidRows([]);
    setFailedRows([]);
    setActiveTab("ready");
    setIsDragging(false);
    setUploading(false);
  };

  const { addSocialMediaBulk, isAddingSocialMediaBulk } = useAddSocialMediaBulk(
    (items) => {
      const existingSet = new Set(
        existingUsernames.map((u) => u.toLowerCase())
      );
      const seen = new Set<string>();
      const valid: IAddSocialMediaBulkResponseRow[] = [];
      const failed: IAddSocialMediaBulkResponseRow[] = [];

      items.forEach((item) => {
        const usernameLower = item.username?.toLowerCase() || "";
        const platformLower = item.platform?.toLowerCase() || "";
        const key = `${platformLower}:${usernameLower}`;

        if (existingSet.has(usernameLower)) {
          failed.push({
            ...item,
            message: "Already present in workspace checks",
          });
        } else if (seen.has(key)) {
          failed.push({
            ...item,
            message: "Duplicate row in CSV",
          });
        } else if (item.status === "success") {
          seen.add(key);
          valid.push(item);
        } else {
          failed.push(item);
        }
      });

      setValidRows(valid);
      setFailedRows(failed);
      setActiveTab(valid.length > 0 ? "ready" : "failed");
    }
  );

  const isProcessing = uploading || isAddingSocialMediaBulk;

  const handleFileSelect = async (selectedFile: File) => {
    const extension = selectedFile.name
      .toLowerCase()
      .substring(selectedFile.name.lastIndexOf("."));
    if (extension !== ".csv") {
      ToastService.error("Please upload a CSV file");
      return;
    }

    setFile(selectedFile);
    try {
      setUploading(true);
      const csvUrl = await FirebaseService.uploadFileToFirebase(
        "ai-workspace/creator-fit/bulk-creators",
        selectedFile
      );
      setUploading(false);
      addSocialMediaBulk({ addBy: { csv: csvUrl } });
    } catch {
      ToastService.error("Failed to upload CSV. Please try again.");
      resetState();
    } finally {
      setUploading(false);
    }
  };

  const handleModalClose = () => {
    if (isImporting || isProcessing) return;
    resetState();
    onClose();
  };

  const handleImportSubmit = async () => {
    if (validRows.length === 0) return;
    const creatorsToImport: IParsedCreatorRow[] = validRows.map((r) => ({
      username: r.username,
      platform: r.platform?.toLowerCase() === "youtube" ? "youtube" : "instagram",
    }));
    await onImport(creatorsToImport);
    resetState();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleModalClose}
      title="Upload Creators Sheet"
      footer={null}
      width={600}
      className={modalStyles.modal}
      destroyOnClose
      styles={{
        body: { padding: 0 },
        header: { marginBottom: 0, padding: 20 },
        content: { padding: 0 },
      }}
    >
      <div className={modalStyles.modalContent}>
        <div className={modalStyles.modalBody}>
          <div className={uploadStyles.content}>
            <p className={uploadStyles.description}>
              Upload a CSV file with platform and profile links (Instagram or YouTube).
            </p>

            <div className={uploadStyles.templateCard}>
              <p className={uploadStyles.templateLabel}>Need a starting format?</p>
              <div className={uploadStyles.templateActions}>
                <button
                  type="button"
                  className={uploadStyles.templateLink}
                  onClick={downloadCreatorFitTemplateCsv}
                >
                  <Download size={14} /> Download sample CSV
                </button>
              </div>
            </div>

            {!file ? (
              <div
                className={`${uploadStyles.dropZone} ${
                  isDragging ? uploadStyles.dropZoneActive : ""
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const dropped = e.dataTransfer.files[0];
                  if (dropped) handleFileSelect(dropped);
                }}
              >
                <div className={uploadStyles.dropZoneIcon}>
                  <FileSpreadsheet size={32} />
                </div>
                <h4 className={uploadStyles.dropZoneTitle}>Drag and drop your file here</h4>
                <p className={uploadStyles.dropZoneSubtitle}>Supports .csv</p>
                <label className={uploadStyles.browseButton}>
                  Browse Files
                  <input
                    type="file"
                    ref={fileInputRef}
                    className={uploadStyles.hiddenInput}
                    accept=".csv"
                    onChange={(e) => {
                      const selected = e.target.files?.[0];
                      if (selected) handleFileSelect(selected);
                    }}
                  />
                </label>
              </div>
            ) : (
              <div>
                <div className={uploadStyles.fileInfo}>
                  <FileSpreadsheet size={16} />
                  <span className={uploadStyles.fileName}>{file.name}</span>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<Trash2 size={14} />}
                    onClick={resetState}
                    disabled={isImporting || isProcessing}
                  />
                </div>

                {isProcessing ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "36px 16px",
                      gap: 12,
                    }}
                  >
                    <Spin size="default" />
                    <span style={{ fontSize: 13, color: "#697386" }}>
                      {uploading
                        ? "Uploading CSV to storage..."
                        : "Validating creators via CSV..."}
                    </span>
                  </div>
                ) : (
                  <CreatorRowsPreview
                    validRows={validRows}
                    failedRows={failedRows}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className={modalStyles.modalFooter}>
          <Button
            onClick={handleModalClose}
            type="text"
            className={modalStyles.cancelButton}
            disabled={isImporting || isProcessing}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className={modalStyles.importButton}
            disabled={validRows.length === 0 || isImporting || isProcessing}
            loading={isImporting}
            onClick={handleImportSubmit}
          >
            {isImporting
              ? "Adding Creators..."
              : `Analyze ${validRows.length} Creator${validRows.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
